// Admin → Tags assign API client. One write: the assign action, proxied to
// the source dashboard's single staticTags update. The table itself stays
// SSE-fed (apis/adminTagsStream.ts); the next snapshot reflects the change.

import { authedFetch, UnauthorizedError } from '@apis/auth';

/** A certificate in an assign/edit body. `expirationDate` is 'YYYY-MM-DD';
 *  `creationDate` rides along only when resending an existing certificate. */
export interface ITagAssignCertificate {
  name: string;
  expirationDate: string;
  creationDate?: string;
}

/** One row of the project's certificate-name catalog (autocomplete source). */
export interface ICertificateCatalogEntry {
  name: string;
  groupName: string;
  isCommonlyUsed: boolean;
}

export type AssignTagInput =
  | {
      kind: 'worker';
      professionId: string;
      name: string;
      idNumber: string;
      phone: string | null;
      certificates?: ITagAssignCertificate[];
    }
  | { kind: 'manager'; managerId: string; certificates?: ITagAssignCertificate[] }
  | { kind: 'guest' }
  | { kind: 'hoist' }
  | { kind: 'scissorsLift'; professionId: string; name: string };

/** The tag has no live presence — powered off; upstream refuses assignment. */
export class TagOfflineError extends Error {
  constructor() {
    super('tag has no live presence');
    this.name = 'TagOfflineError';
  }
}

/** One-manager-one-tag rule: the picked manager already holds another tag. */
export class ManagerTagTakenError extends Error {
  constructor() {
    super('manager already assigned to another tag');
    this.name = 'ManagerTagTakenError';
  }
}

async function parseErrors(res: Response): Promise<never> {
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  const error = body?.error as Record<string, unknown> | undefined;
  const code = error?.code as string | undefined;
  if (res.status === 401) throw new UnauthorizedError();
  if (res.status === 409 && code === 'TAG_OFFLINE') throw new TagOfflineError();
  if (res.status === 409 && code === 'MANAGER_TAG_TAKEN') throw new ManagerTagTakenError();
  throw new Error(`request failed: ${res.status} ${(error?.message as string | undefined) ?? ''}`);
}

export async function fetchTagCertificatesCatalog(
  siteId: string,
  signal?: AbortSignal,
): Promise<{ certificates: ICertificateCatalogEntry[] }> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/admin/tags/certificates`,
    { signal },
  );
  if (res.ok) return (await res.json()) as { certificates: ICertificateCatalogEntry[] };
  return parseErrors(res);
}

export async function fetchHoistAvailability(
  siteId: string,
  signal?: AbortSignal,
): Promise<{ hasHoistFloor: boolean }> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/admin/tags/hoist-availability`,
    { signal },
  );
  if (res.ok) return (await res.json()) as { hasHoistFloor: boolean };
  return parseErrors(res);
}

export async function unassignTag(siteId: string, tagId: string): Promise<void> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/admin/tags/${encodeURIComponent(tagId)}/unassign`,
    { method: 'POST' },
  );
  if (res.ok) return;
  return parseErrors(res);
}

export async function assignTag(
  siteId: string,
  tagId: string,
  input: AssignTagInput,
): Promise<void> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/admin/tags/${encodeURIComponent(tagId)}/assign`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    },
  );
  if (res.ok) return;
  return parseErrors(res);
}